import { z } from 'zod'

/**
 * A song's place in a music catalog (Apple Music now; Spotify may follow):
 * what a link says about the recording, and the rules for who may change it.
 *
 * - `matched`: found automatically, confidently.
 * - `suggested`: found automatically, not confidently: shown for a person to confirm.
 * - `unmatched`: looked for automatically and not found; tried again nightly.
 * - `confirmed`: a person chose it (or confirmed a suggestion).
 * - `none`: a person said it is not in the catalog.
 *
 * The automatic matcher never changes a `confirmed` or `none` link: what a
 * person decided stays decided until a person changes it.
 */

export const CATALOG_PROVIDERS = ['apple_music', 'spotify'] as const
export type CatalogProvider = (typeof CATALOG_PROVIDERS)[number]

export const CATALOG_LINK_STATES = [
  'matched',
  'suggested',
  'unmatched',
  'confirmed',
  'none',
] as const
export type CatalogLinkState = (typeof CATALOG_LINK_STATES)[number]

/** A person decided it: the automatic matcher leaves it alone. */
export function decidedByPerson(state: CatalogLinkState): boolean {
  return state === 'confirmed' || state === 'none'
}

/** A link with a recording behind it (artwork, a preview). */
export function hasRecording(state: CatalogLinkState): boolean {
  return state === 'matched' || state === 'suggested' || state === 'confirmed'
}

/** Album art, as the catalog describes it: a URL template and its colours. */
export interface CatalogArtwork {
  /** A template with `{w}` and `{h}` (Apple's): `artworkUrl` fills them. */
  url: string
  width: number | null
  height: number | null
  /** Hex without '#': the colour behind the art while it loads. */
  bgColor: string | null
  textColor1: string | null
  textColor2: string | null
  textColor3: string | null
  textColor4: string | null
}

/** One recording in a catalog, as kept on a song's link (and shown). */
export interface CatalogSong {
  id: string
  name: string
  artistName: string
  albumName: string | null
  composerName: string | null
  artwork: CatalogArtwork | null
  /** A 30-second preview anyone may hear, without a subscription. */
  previewUrl: string | null
  durationMs: number | null
  isrc: string | null
  genreNames: string[]
  /** YYYY-MM-DD, or YYYY. */
  releaseDate: string | null
  /** The recording in the catalog's own app: what "Apple Music" links to. */
  url: string | null
  /** 'explicit' or 'clean'; null when unrated. */
  contentRating: string | null
}

/** An editorial playlist in the catalog (Apple Music's own, by a curator). */
export interface CatalogPlaylist {
  /** "pl.…". */
  id: string
  name: string
  /** "Apple Music Christian". */
  curatorName: string | null
  artwork: CatalogArtwork | null
  /** The playlist in Apple Music. */
  url: string | null
  /** Apple's short note about it, when it has one. */
  description: string | null
}

/** The album a recording is on, as far as a new song needs it. */
export interface CatalogAlbum {
  id: string
  name: string
  /** "℗ 2025 Elevation Worship Records". */
  copyright: string | null
  recordLabel: string | null
}

/**
 * Apple Music storefronts a church is most likely in (ISO 3166 alpha-2, lower
 * case), offered in settings; Apple has more, and any two-letter code it
 * knows works.
 */
export const COMMON_STOREFRONTS = [
  'us',
  'ca',
  'gb',
  'ie',
  'au',
  'nz',
  'za',
  'ng',
  'gh',
  'ke',
  'ug',
  'in',
  'ph',
  'sg',
  'my',
  'kr',
  'jp',
  'br',
  'mx',
  'ar',
  'co',
  'cl',
  'pe',
  'de',
  'fr',
  'es',
  'pt',
  'it',
  'nl',
  'be',
  'ch',
  'at',
  'se',
  'no',
  'dk',
  'fi',
  'pl',
  'ro',
  'ua',
] as const

/** A preview's length: Apple's are 30 seconds. */
export const PREVIEW_SECONDS = 30

/**
 * An artwork template at a size, in pixels: `{w}x{h}bb.jpg` → `120x120bb.jpg`.
 * Square unless `height` is given; `{c}` (crop) and `{f}` (format) too.
 */
export function artworkUrl(template: string, width: number, height = width): string {
  const w = String(Math.max(1, Math.round(width)))
  const h = String(Math.max(1, Math.round(height)))
  return template
    .replaceAll('{w}', w)
    .replaceAll('{h}', h)
    .replaceAll('{c}', 'bb')
    .replaceAll('{f}', 'jpg')
}

/** "2019" from "2019-03-08" (or "2019"); null when unknown. */
export function releaseYear(releaseDate: string | null): string | null {
  const match = /^(\d{4})/.exec(releaseDate ?? '')
  return match?.[1] ?? null
}

/** "#1a2b3c" from "1a2b3c", or null when it is not a colour. */
export function hexColor(value: string | null | undefined): string | null {
  return value && /^[0-9a-f]{6}$/i.test(value) ? `#${value}` : null
}

const nullableText = z.string().nullable().catch(null)
const catalogSongSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  artistName: z.string().catch(''),
  albumName: nullableText,
  composerName: nullableText,
  artwork: z
    .object({
      url: z.string().min(1),
      width: z.number().nullable().catch(null),
      height: z.number().nullable().catch(null),
      bgColor: nullableText,
      textColor1: nullableText,
      textColor2: nullableText,
      textColor3: nullableText,
      textColor4: nullableText,
    })
    .nullable()
    .catch(null),
  previewUrl: nullableText,
  durationMs: z.number().int().nonnegative().nullable().catch(null),
  isrc: nullableText,
  genreNames: z.array(z.string()).catch([]),
  releaseDate: nullableText,
  url: nullableText,
  contentRating: nullableText,
})

/** A stored recording read back (a link's `metadata`), or null when it is not one. */
export function parseCatalogSong(value: unknown): CatalogSong | null {
  const parsed = catalogSongSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}
